/**
 * F-242 / F-243 / F-244 / F-245 — Texas Flood Compliance dashboard (E-043).
 *
 * Texas-market gated (registered under /texas/flood). Drives the canonical
 * flood contract via floodApi (src/services/floodApi.ts):
 *   • Property flood-zone panel — lookup by lat/lon or address, show the
 *     zone determination + freshness, and persist it as the property record.
 *   • 5-year flood history view — ingest source feeds and list recorded events.
 *   • Flood disclosure flow — generate/issue the bilingual F-245 notice, then
 *     capture the signed acknowledgment into the evidence vault.
 *
 * floodApi never throws (fail-soft), so this page renders partial data and
 * only surfaces a hint when a call genuinely returns nothing.
 */
import React, { useEffect, useMemo, useState } from 'react';
import floodApi, {
    FloodAcknowledgment,
    FloodMeta,
    FloodNotice,
    FloodZoneLookup,
    FloodZoneRecord,
} from '../../services/floodApi';

const FLOOD_SOURCE_LABELS: Record<string, string> = {
    noaa_storm_events: 'NOAA Storm Events',
    fema_disaster_declaration: 'FEMA Disaster Declarations',
    nfip_claim: 'NFIP Claims',
};

export const FloodComplianceDashboard: React.FC = () => {
    const [meta, setMeta] = useState<FloodMeta | null>(null);
    const [propertyId, setPropertyId] = useState('');
    const [address, setAddress] = useState('');
    const [lat, setLat] = useState('');
    const [lon, setLon] = useState('');

    const [lookup, setLookup] = useState<FloodZoneLookup | null>(null);
    const [record, setRecord] = useState<FloodZoneRecord | null>(null);
    const [years, setYears] = useState<FloodZoneRecord[]>([]);

    const [eventRows, setEventRows] = useState<Array<{ occurredAt: string; source: string; eventType: string | null; severity: string | null }>>([]);
    const [historySummary, setHistorySummary] = useState<string | null>(null);

    const [notice, setNotice] = useState<FloodNotice | null>(null);
    const [signerName, setSignerName] = useState('');
    const [signerRole, setSignerRole] = useState('');
    const [ack, setAck] = useState<FloodAcknowledgment | null>(null);

    const [busy, setBusy] = useState<string | null>(null);
    const [hint, setHint] = useState<string | null>(null);

    useEffect(() => {
        floodApi.getMeta().then((m) => setMeta(m));
    }, []);

    const zoneReady = useMemo(() =>
        Boolean(propertyId.trim()) && Boolean(lookup && lookup.zoneCode),
        [propertyId, lookup]);

    const run = async (key: string, fn: () => Promise<void>) => {
        setBusy(key);
        setHint(null);
        try {
            await fn();
        } finally {
            setBusy(null);
        }
    };

    const lookupByAddress = () =>
        run('lookup', async () => {
            const result = await floodApi.lookupByAddress(address.trim());
            setLookup(result);
            setHint(result && result.zoneCode
                ? 'Zone determination loaded.'
                : 'No NFHL zone could be resolved for that address (degraded or unmapped).');
        });

    const lookupByLatLon = () =>
        run('lookup', async () => {
            const result = await floodApi.lookupByLatLon(lat.trim(), lon.trim());
            setLookup(result);
            setHint(result && result.zoneCode
                ? 'Zone determination loaded.'
                : 'No NFHL zone could be resolved for that coordinate (degraded or unmapped).');
        });

    const saveDetermination = () =>
        run('zone', async () => {
            if (!lookup) {
                setHint('Look up a zone before persisting it.');
                return;
            }
            const saved = await floodApi.saveZone({
                propertyId: propertyId.trim(),
                zoneCode: lookup.zoneCode ?? undefined,
                zoneClassification: lookup.zoneClassification,
                inFloodplain: lookup.inFloodplain,
                source: lookup.source,
                status: lookup.status,
                lat: lookup.lat,
                lon: lookup.lon,
                panelNumber: lookup.panelNumber,
                communityNumber: lookup.communityNumber,
                effectiveDate: lookup.effectiveDate,
            } as Partial<FloodZoneRecord>);
            setRecord(saved ?? null);
            setHint(saved ? `Determination saved (${saved.partition ?? saved.pk ?? 'partition'}).` : 'Could not save the determination.');
            await loadZone();
            await loadYears();
        });

    const loadZone = async () => {
        if (!propertyId.trim()) {
            setHint('Enter a property id to load its flood zone record.');
            return;
        }
        const current = await floodApi.getZone(propertyId.trim());
        setRecord(current);
        setHint(current
            ? `Loaded ${current.stale ? 'a STALE' : 'the current'} determination for ${current.propertyId}.`
            : 'No flood zone record on file for this property yet.');
    };

    const loadYears = async () => {
        if (!propertyId.trim()) return;
        setYears(await floodApi.listYears(propertyId.trim()));
    };

    const loadHistory = () =>
        run('history', async () => {
            if (!propertyId.trim()) {
                setHint('Enter a property id to load its 5-year flood history.');
                return;
            }
            const history = await floodApi.getHistory(propertyId.trim());
            setEventRows(history.events || []);
            setHistorySummary(history.hasHistory
                ? `${history.eventCount} flood event(s) recorded in the past ${history.windowYears} years.`
                : `No flood events recorded in the past ${history.windowYears} years.`);
        });

    const ingestHistory = () =>
        run('history', async () => {
            if (!propertyId.trim()) {
                setHint('Enter a property id to ingest flood history.');
                return;
            }
            const result = await floodApi.ingestHistory(propertyId.trim(), {});
            setHistorySummary(result
                ? `Ingested ${result.collected} event(s); ${result.rejected} rejected (window ${result.windowYears}y).`
                : 'History ingest reported nothing.');
            await loadHistory();
        });

    const issueNotice = () =>
        run('notice', async () => {
            if (!propertyId.trim()) {
                setHint('Enter a property id to generate the flood disclosure notice.');
                return;
            }
            const issued = await floodApi.issueNotice(propertyId.trim());
            setNotice(issued);
            setAck(null);
            setSignerName('');
            setSignerRole('');
            setHint(issued
                ? `Notice ${issued.noticeId} filed to ${issued.evidenceVault}.`
                : 'Notice generation returned nothing — the determination may be incomplete or degraded.');
        });

    const captureAck = () =>
        run('ack', async () => {
            if (!notice) return;
            if (!signerName.trim()) {
                setHint('A signer name is required for the acknowledgment.');
                return;
            }
            const captured = await floodApi.acknowledge(notice.noticeId, {
                signerName: signerName.trim(),
                signerRole: signerRole.trim() || undefined,
                method: 'typed_name',
            });
            setAck(captured);
            setHint(captured
                ? `Acknowledgment ${captured.acknowledgmentId} captured to ${captured.evidenceVault}.`
                : 'Acknowledgment could not be captured.');
        });

    const freshness = record && record.refreshedAt
        ? `${record.stale ? 'STALE' : 'current'} · refreshed ${new Date(record.refreshedAt).toLocaleString()}`
        : 'not refreshed yet';

    return (
        <div className="texas-page-container" data-testid="flood-page">
            <h1>Flood Compliance</h1>
            <p className="page-description" data-testid="flood-description">
                {meta && meta.citation ? meta.citation : 'Texas flood compliance'} — NFHL zone determinations, 5-year
                flood history, and the {meta ? `F-${meta.features.join(' / F-')}` : 'disclosure'} notice flow.
            </p>

            {hint && <div className="notice-box" data-testid="flood-hint">{hint}</div>}

            <div className="result-grid">
                <div className="result-card" data-testid="flood-meta-features">
                    <strong>{meta ? meta.features.length : '—'}</strong>
                    <span>Flood features</span>
                </div>
                <div className="result-card" data-testid="flood-meta-refresh">
                    <strong>{meta ? meta.refreshCycleYears : '—'}</strong>
                    <span>Year refresh cycle</span>
                </div>
                <div className="result-card" data-testid="flood-meta-vault">
                    <strong>{meta ? meta.vault.split('-').slice(0, 2).join('') : '—'}</strong>
                    <span>Evidence vault</span>
                </div>
            </div>

            <div className="result-section">
                <h2>Property flood zone</h2>
                <div className="flex flex-wrap items-end gap-3">
                    <label className="flex flex-col text-xs text-gray-600">
                        Property ID
                        <input
                            className="lease-input"
                            value={propertyId}
                            onChange={(e) => setPropertyId(e.target.value)}
                            placeholder="prp_123"
                            data-testid="flood-property-input"
                        />
                    </label>
                    <label className="flex flex-col text-xs text-gray-600">
                        Address
                        <input
                            className="lease-input"
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                            placeholder="123 Main St, Austin TX"
                            data-testid="flood-address-input"
                        />
                    </label>
                    <button
                        className="lease-input-btn"
                        onClick={lookupByAddress}
                        disabled={!address.trim() || busy === 'lookup'}
                        data-testid="flood-lookup-address"
                    >
                        Look up by address
                    </button>
                    <label className="flex flex-col text-xs text-gray-600">
                        Latitude
                        <input
                            className="lease-input"
                            value={lat}
                            onChange={(e) => setLat(e.target.value)}
                            placeholder="29.7604"
                            data-testid="flood-lat-input"
                        />
                    </label>
                    <label className="flex flex-col text-xs text-gray-600">
                        Longitude
                        <input
                            className="lease-input"
                            value={lon}
                            onChange={(e) => setLon(e.target.value)}
                            placeholder="-95.3698"
                            data-testid="flood-lon-input"
                        />
                    </label>
                    <button
                        className="lease-input-btn"
                        onClick={lookupByLatLon}
                        disabled={(!lat.trim() || !lon.trim()) && busy === 'lookup'}
                        data-testid="flood-lookup-latlon"
                    >
                        Look up by lat/lon
                    </button>
                </div>

                {lookup && (
                    <div className="result-card" data-testid="flood-lookup-result">
                        {lookup.degraded && <p className="red-warning">Determination degraded: {lookup.degradedDetail || lookup.degradedMode}</p>}
                        <p><strong>Zone:</strong> {lookup.zoneCode || 'unmapped'} {lookup.zoneSubtype ? `(${lookup.zoneSubtype})` : ''}</p>
                        <p><strong>Classification:</strong> {lookup.zoneClassification || '—'}</p>
                        <p><strong>Floodplain:</strong> <span className={lookup.inFloodplain ? 'red-warning' : 'success'}>{lookup.inFloodplain ? 'IN FLOODPLAIN' : 'Outside floodplain'}</span></p>
                        {lookup.panelNumber && <p><strong>FEMA panel:</strong> {lookup.panelNumber}</p>}
                        {lookup.effectiveDate && <p><strong>Effective:</strong> {lookup.effectiveDate}</p>}
                        {zoneReady && (
                            <button
                                className="lease-input-btn"
                                onClick={saveDetermination}
                                disabled={busy === 'zone'}
                                data-testid="flood-save-zone"
                            >
                                Save as determination
                            </button>
                        )}
                    </div>
                )}

                {record && (
                    <div className="result-card" data-testid="flood-record">
                        <p><strong>{record.propertyId}</strong> · {freshness}</p>
                        <p>Zone {record.zoneCode || '—'} · {record.zoneClassification || '—'} · {record.inFloodplain ? 'in floodplain' : 'outside floodplain'}</p>
                        <p className="text-gray-500 text-xs">Source {record.source || '—'} · status {record.status || '—'}</p>
                    </div>
                )}

                <div className="mt-3 flex items-center gap-3">
                    <button className="lease-input-btn" onClick={loadZone} data-testid="flood-load-zone">Load property record</button>
                    <button className="lease-input-btn" onClick={loadYears} data-testid="flood-load-years">Load stored years</button>
                    {years.length > 0 && (
                        <span className="text-gray-500 text-xs" data-testid="flood-years-count">{years.length} year(s) stored</span>
                    )}
                </div>
            </div>

            <div className="result-section">
                <h2>5-year flood history</h2>
                <div className="flex items-center gap-3 mb-3">
                    <button className="lease-input-btn" onClick={loadHistory} disabled={!propertyId.trim() || busy === 'history'} data-testid="flood-load-history">Load history</button>
                    <button className="lease-input-btn" onClick={ingestHistory} disabled={!propertyId.trim() || busy === 'history'} data-testid="flood-ingest-history">Ingest history</button>
                </div>
                {historySummary && <p className="text-gray-600 text-sm" data-testid="flood-history-summary">{historySummary}</p>}
                {eventRows.length > 0 ? (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-gray-500 border-b">
                                <th className="py-2 pr-4">When</th>
                                <th className="py-2 pr-4">Source</th>
                                <th className="py-2 pr-4">Type</th>
                                <th className="py-2">Severity</th>
                            </tr>
                        </thead>
                        <tbody>
                            {eventRows.map((e, i) => (
                                <tr key={`${e.occurredAt}-${i}`} className="border-b last:border-0" data-testid="flood-history-row">
                                    <td className="py-2 pr-4">{new Date(e.occurredAt).toLocaleDateString()}</td>
                                    <td className="py-2 pr-4">{FLOOD_SOURCE_LABELS[e.source] || e.source}</td>
                                    <td className="py-2 pr-4">{e.eventType || '—'}</td>
                                    <td className="py-2">{e.severity || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    historySummary && <p className="no-results" data-testid="flood-history-empty">No flood events on file.</p>
                )}
            </div>

            <div className="result-section">
                <h2>Flood disclosure notice</h2>
                <button className="lease-input-btn" onClick={issueNotice} disabled={!propertyId.trim() || busy === 'notice'} data-testid="flood-issue-notice">
                    {busy === 'notice' ? 'Generating…' : 'Generate &amp; issue notice'}
                </button>

                {notice && (
                    <div className="result-card" data-testid="flood-notice">
                        <p><strong>{notice.noticeId}</strong> · {notice.isComplete ? <span className="success">complete</span> : <span className="red-warning">incomplete</span>}</p>
                        <p>{notice.determination.zoneCode || 'unmapped'} · {notice.determination.inFloodplain ? 'IN FLOODPLAIN' : 'outside floodplain'} · panel {notice.determination.panelNumber || '—'}</p>
                        <p className="text-sm text-gray-700" data-testid="flood-notice-en">{notice.text.en.zoneStatement}</p>
                        <p className="text-sm text-gray-700" data-testid="flood-notice-es">{notice.text.es.zoneStatement}</p>
                        <p className="text-sm text-gray-600">{notice.text.en.insuranceStatement}</p>
                        {notice.panelMap.url && (
                            <p className="text-xs">
                                <a href={notice.panelMap.url} target="_blank" rel="noopener noreferrer" data-testid="flood-notice-panel">FEMA panel map</a>
                            </p>
                        )}
                        <div className="mt-3 flex flex-wrap items-end gap-3">
                            <label className="flex flex-col text-xs text-gray-600">
                                Signer name
                                <input
                                    className="lease-input"
                                    value={signerName}
                                    onChange={(e) => setSignerName(e.target.value)}
                                    placeholder="Jane Buyer"
                                    data-testid="flood-signer-input"
                                />
                            </label>
                            <label className="flex flex-col text-xs text-gray-600">
                                Role (optional)
                                <input
                                    className="lease-input"
                                    value={signerRole}
                                    onChange={(e) => setSignerRole(e.target.value)}
                                    placeholder="Buyer / Tenant"
                                    data-testid="flood-role-input"
                                />
                            </label>
                            <button className="lease-input-btn" onClick={captureAck} disabled={!notice.isComplete || busy === 'ack'} data-testid="flood-capture-ack">
                                Capture signed acknowledgment
                            </button>
                        </div>
                    </div>
                )}

                {ack && (
                    <div className="result-card" data-testid="flood-ack">
                        <p><strong>{ack.acknowledgmentId}</strong> signed by {ack.signerName} · {ack.signatureMethod}</p>
                        <p className="text-gray-500 text-xs">{ack.evidenceVault} · {ack.s3Reference}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FloodComplianceDashboard;